import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  NumberField,
  ReferenceField,
} from 'react-admin';
import { useListController, useListFilterContext, useTranslate } from 'ra-core';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BaseDrawer from '../resources/offers/offerDrawer/BaseDrawer';
import OfferDrawerContent from '../resources/offers/offerDrawer/OfferDrawerContent';
import { useState } from 'react';
import { Offer } from '../resources/offers/types';

const OfferPerformanceGrid = () => {
  const { filterValues } = useListFilterContext();
  const offersByOfferController = useListController({
    resource: 'reports/offers-by-offer',
    filter: filterValues,
    perPage: Infinity,
    sort: { field: 'offer_id', order: 'ASC' },
    disableSyncWithLocation: true,
  });
  const t = useTranslate();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);

  const handleIconClick = (offer: Offer) => {
    setSelectedOffer(offer);
    setDrawerOpened(true);
  };

  const handleCloseOfferDrawer = () => {
    setDrawerOpened(false);
    setSelectedOffer(null);
  };

  return (
    <ListContextProvider value={offersByOfferController}>
      <div className="rounded-lg border border-border bg-card w-full">
        <div className="px-6 pb-2 flex flex-col items-start">
          <div className="w-full">
            <p className="text-lg font-semibold my-4">
              {t('reporting.offer_performance')}
            </p>
            <Datagrid
              bulkActionButtons={false}
              sx={{
                '& .RaDatagrid-headerCell': {
                  p: 2,
                  fontWeight: 'bold',
                },
                '& .RaDatagrid-rowCell': {
                  p: 2,
                },
                '& .MuiTableRow-root:last-of-type td': {
                  borderBottom: 'none',
                },
              }}
            >
              <ReferenceField source="offer_id" reference="offers">
                <FunctionField<Offer>
                  label="Offer"
                  render={offer => {
                    return offer?.brand ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="size-10 rounded-md">
                          <AvatarImage
                            src={offer.brand.logo}
                            alt={offer.brand.name}
                          />
                          <AvatarFallback className="rounded-md">
                            {offer.brand.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-1">
                          <p className="text-base truncate">
                            {offer.brand.name}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {offer.name}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  }}
                />
              </ReferenceField>
              <NumberField source="impressions" label="Impressions" />
              <NumberField source="clicks" label="Clicks" />
              {
                //<NumberField source="actions" label="Actions" />
              }
              <FunctionField
                label="Details"
                render={offer => (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleIconClick(offer)}
                  >
                    <Info className="size-4 text-muted-foreground" />
                  </Button>
                )}
              />
            </Datagrid>

            <BaseDrawer open={drawerOpened} onClose={handleCloseOfferDrawer}>
              {selectedOffer && (
                <OfferDrawerContent
                  id={selectedOffer.id}
                  onCreativeOpen={() => {}}
                />
              )}
            </BaseDrawer>
          </div>
        </div>
      </div>
    </ListContextProvider>
  );
};

export default OfferPerformanceGrid;
